import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Trash2, Archive, X } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConfirmVariant = 'info' | 'warning' | 'danger';

export interface ConfirmAction {
  label: string;
  variant: 'neon' | 'magenta' | 'danger' | 'ghost';
  result: string; // returned value when this action is clicked
  icon?: ReactNode;
}

export interface ConfirmOptions {
  title: string;
  description?: string;
  variant?: ConfirmVariant;
  /**
   * Multi-action confirm: e.g. for delete UX with 2 options (archive vs hard-delete).
   * If omitted, defaults to a simple Cancel + Confirm pair.
   */
  actions?: ConfirmAction[];
  confirmLabel?: string;
  cancelLabel?: string;
}

// Returns the result string of the chosen action (or null if cancelled).
type ConfirmFn = (options: ConfirmOptions) => Promise<string | null>;

// ─── Context ──────────────────────────────────────────────────────────────────

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface DialogState {
  open: boolean;
  options: ConfirmOptions;
  resolve: ((value: string | null) => void) | null;
}

const initialState: DialogState = {
  open: false,
  options: { title: '' },
  resolve: null,
};

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState>(initialState);

  const confirm: ConfirmFn = useCallback((options) => {
    return new Promise<string | null>((resolve) => {
      setState({ open: true, options, resolve });
    });
  }, []);

  const handleAction = useCallback(
    (result: string | null) => {
      state.resolve?.(result);
      setState(initialState);
    },
    [state],
  );

  // Esc to cancel
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleAction(null);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [state.open, handleAction]);

  const opts = state.options;
  const variant = opts.variant ?? 'info';
  const variantConfig = {
    info: {
      ringColor: 'border-neon-cyan/40',
      iconBg: 'bg-neon-cyan/10 border-neon-cyan',
      iconColor: 'text-neon-cyan',
      title: 'text-neon-cyan',
    },
    warning: {
      ringColor: 'border-neon-yellow/40',
      iconBg: 'bg-neon-yellow/10 border-neon-yellow',
      iconColor: 'text-neon-yellow',
      title: 'text-neon-yellow',
    },
    danger: {
      ringColor: 'border-neon-red/40',
      iconBg: 'bg-neon-red/10 border-neon-red',
      iconColor: 'text-neon-red',
      title: 'text-neon-red',
    },
  }[variant];

  // Default actions when not provided
  const actions: ConfirmAction[] =
    opts.actions ?? [
      {
        label: opts.confirmLabel ?? 'Confirm',
        variant: variant === 'danger' ? 'danger' : 'neon',
        result: 'confirm',
      },
    ];

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state.open && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in"
          onClick={() => handleAction(null)}
        >
          <div
            className={cn(
              'cyber-panel w-full max-w-md p-0 animate-slide-up overflow-hidden',
              variantConfig.ringColor,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 p-6">
              <div
                className={cn(
                  'shrink-0 w-12 h-12 rounded border flex items-center justify-center',
                  variantConfig.iconBg,
                  variantConfig.iconColor,
                )}
              >
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h2
                  className={cn(
                    'font-display text-lg font-bold uppercase tracking-wider mb-1',
                    variantConfig.title,
                  )}
                >
                  {opts.title}
                </h2>
                {opts.description && (
                  <p className="text-sm text-text-secondary leading-relaxed">{opts.description}</p>
                )}
              </div>
              <button
                onClick={() => handleAction(null)}
                className="text-text-tertiary hover:text-neon-red transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex flex-row gap-2 justify-end p-3 border-t border-border-subtle bg-bg-tertiary/50">
              <Button variant="ghost" size="sm" onClick={() => handleAction(null)}>
                {opts.cancelLabel ?? 'Cancel'}
              </Button>
              {actions.map((action, i) => (
                <Button
                  key={i}
                  variant={action.variant}
                  size="sm"
                  icon={action.icon}
                  onClick={() => handleAction(action.result)}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </ConfirmContext.Provider>
  );
}

// ─── Convenience presets ─────────────────────────────────────────────────────

export const archiveAndDeleteActions: ConfirmAction[] = [
  {
    label: 'Archive',
    variant: 'neon',
    result: 'archive',
    icon: <Archive size={14} />,
  },
  {
    label: 'Delete Permanently',
    variant: 'danger',
    result: 'delete',
    icon: <Trash2 size={14} />,
  },
];
